/**
 * @file App.tsx
 * @description
 * The main application component that sets up routing and provides authentication context.
 * We have added a new protected route for '/submissions/:id' that renders SubmissionDetails.
 * 
 * Key responsibilities:
 * - Render a set of routes (dashboard, admin, auth, etc.).
 * - Protect certain routes to only allow authenticated users or admins.
 * 
 * @dependencies
 * - react-router-dom: For routing
 * - useAuth custom hook: Provides user and role
 * 
 * @notes
 * - We appended a new route: '/submissions/:id' => <SubmissionDetails />
 * - The <ProtectedRoute> ensures only logged-in users can view.
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import UserDashboard from './pages/UserDashboard';
import AdminDashboard from './pages/AdminDashboard';
import { SubmissionForm } from './pages/SubmissionFormDraft';
import { SubmissionForm as SubmissionFormNeedsAction } from './pages/SubmissionFormNeedsAction';
import Billing from './pages/Billing';
import SubmissionDetails from './pages/SubmissionDetails';
import SubmissionFormReadOnly from './pages/SubmissionFormReadOnly';
import AdminReview from './pages/AdminReview';
import ViewAuditLog from './pages/ViewAuditLog';
import MagicLinkRequest from './pages/MagicLinkRequest';
import ProfileSettings from './pages/ProfileSettings';
import { SupabaseProvider } from 'contexts/SupabaseContext';
import { IdleTimerProvider } from './contexts/IdleTimerContext';
import SessionTimeoutModal from './components/SessionTimeoutModal';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'USER' | 'ADMIN';
}

/**
 * ProtectedRoute
 * --------------
 * Checks if the user is authenticated and, if required, matches the required role.
 * If the user is not authenticated or does not match the required role,
 * it redirects accordingly.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { user, userRole, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/signin" />;
  }

  // If a specific role is required and user's role doesn't match
  if (requiredRole && userRole !== requiredRole) {
    // Redirect admin to admin dashboard
    if (userRole === 'ADMIN') {
      return <Navigate to="/admin" />;
    }
    // Redirect regular users to user dashboard
    return <Navigate to="/" />;
  }
  
  return <>{children}</>;
};

/**
 * DefaultRedirect
 * ---------------
 * A fallback route that redirects based on user role (admin vs user).
 */
const DefaultRedirect: React.FC = () => {
  const { userRole } = useAuth();
  return <Navigate to={userRole === 'ADMIN' ? '/admin' : '/'} />;
};

/**
 * PublicRoutes
 * ------------
 * Contains routes that don't require authentication
 */
const PublicRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/auth" element={<Navigate to="/signin" />} />
      <Route path="/magic-link-request" element={<MagicLinkRequest />} />
      
      {/* Root path redirects to signin for non-logged-in users */}
      <Route path="/" element={<Navigate to="/signin" />} />
      
      {/* Catch-all redirects to signin */}
      <Route path="*" element={<Navigate to="/signin" />} />
    </Routes>
  );
};

/**
 * ProtectedRoutes
 * ---------------
 * Contains routes that require authentication, wrapped with IdleTimerProvider
 */
const ProtectedRoutes: React.FC = () => {
  const { user } = useAuth();
  
  // If no user, don't render anything
  if (!user) {
    return null;
  }
  
  return (
    <IdleTimerProvider>
      <Routes>
        {/* Default user route */}
        <Route
          path="/"
          element={
            <ProtectedRoute requiredRole="USER">
              <UserDashboard />
            </ProtectedRoute>
          }
        />

        {/* Protected routes for normal users */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute requiredRole="USER">
              <UserDashboard />
            </ProtectedRoute>
          }
        />
        
        {/* Profile settings route */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfileSettings />
            </ProtectedRoute>
          }
        />
        
         {/* Route for creating new submissions or editing submissions if status is DRAFT */}
        <Route
          path="/new-submission"
          element={
            <ProtectedRoute requiredRole="USER">
              <SubmissionForm />
            </ProtectedRoute>
          }
        />
        {/* Route for editing submissions if status is NEEDS_ACTION */}
        <Route
          path="/edit-submission"
          element={
            <ProtectedRoute requiredRole="USER">
              <SubmissionFormNeedsAction />
            </ProtectedRoute>
          }
        />

        {/* Route for read-only view of submissions if status is IRB_REVIEW or DECISION_ISSUED*/}
        <Route
          path="/view-submission"
          element={
            <ProtectedRoute requiredRole="USER">
              <SubmissionFormReadOnly />
            </ProtectedRoute>
          }
        />

        {/* Route for viewing submission details */}
        <Route
          path="/submissions/:id"
          element={
            <ProtectedRoute requiredRole="USER">
              <SubmissionDetails />
            </ProtectedRoute>
          }
        />

        {/* Route for billing */}
        <Route
          path="/billing"
          element={
            <ProtectedRoute requiredRole="USER">
              <Billing />
            </ProtectedRoute>
          }
        />
        
        {/* Admin route */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* Admin route to view submission details */}
        <Route
          path="/admin/submissions/:id"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <AdminReview />
            </ProtectedRoute>
          }
        />

        {/* Admin route to view audit logs */}
        <Route
          path="/admin/audit-logs"
          element={
            <ProtectedRoute requiredRole="ADMIN">
              <ViewAuditLog />
            </ProtectedRoute>
          }
        />

        {/* Fallback route */}
        <Route 
          path="*" 
          element={
            <ProtectedRoute>
              <DefaultRedirect />
            </ProtectedRoute>
          }
        />
      </Routes>
      <SessionTimeoutModal />
    </IdleTimerProvider>
  );
};

/**
 * RouteManager
 * ------------
 * Manages the rendering of the correct routes based on auth state
 */
const RouteManager: React.FC = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F55D3E]"></div>
      </div>
    );
  }
  
  return user ? <ProtectedRoutes /> : <PublicRoutes />;
};

/**
 * App
 * ---
 * Wraps the entire application in AuthProvider, sets up the main router, and
 * defines the routes for auth, user dashboard, admin dashboard, submission form,
 * and the new submission details page.
 */
const App: React.FC = () => {
  return (
    <SupabaseProvider>
      <AuthProvider>
        <BrowserRouter>
          <RouteManager />
        </BrowserRouter>
      </AuthProvider>
    </SupabaseProvider>
  );
};

export default App;
